import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Lock,
  LockOpen,
  Search,
  ShieldAlert,
  ShieldCheck,
  Users,
  XCircle,
} from "lucide-react";
import { useUserDepartmentOptions, useUsers } from "../hooks";
import { exportToExcel, exportToPdf } from "#/lib/exportUtils";
import type { UserRow } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "bg-emerald-500", "bg-blue-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500", "bg-teal-500", "bg-indigo-500", "bg-orange-500",
];
function avatarColor(name: string) {
  let n = 0; for (const c of name) n += c.charCodeAt(0);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}
function initials(name: string) {
  return name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("");
}

function buildPageNums(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const always = new Set([0, 1, current - 1, current, current + 1, total - 2, total - 1]);
  const inRange = [...always].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const pages: (number | "…")[] = [];
  let prev = -1;
  for (const p of inRange) { if (prev !== -1 && p - prev > 1) pages.push("…"); pages.push(p); prev = p; }
  return pages;
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [search, setSearch] = useState("");
  const [enabled, setEnabled] = useState<boolean | "">("");
  const [locked, setLocked] = useState<boolean | "">("");
  const [departmentCode, setDepartmentCode] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const usersQuery = useUsers({ page, size, search, enabled, locked, departmentCode });
  const deptsQuery = useUserDepartmentOptions();
  const users = usersQuery.data?.content ?? [];
  const totalPages = usersQuery.data?.totalPages ?? 1;
  const pageNums = buildPageNums(page, totalPages);
  const isLoading = usersQuery.isLoading || deptsQuery.isLoading;

  async function handlePdf() {
    setExporting(true);
    try {
      await exportToPdf({
        title: "User Administration",
        subtitle: `${usersQuery.data?.totalElements ?? users.length} users · ${new Date().toLocaleString()}`,
        headers: ["Username", "Full Name", "Email", "Job Title", "Primary Dept", "Enabled", "Locked"],
        rows: users.map((u) => [
          u.username, u.fullName ?? "",  u.email,
          u.employee?.jobTitle ?? "—",
          u.employee?.departmentName ?? u.departments?.[0]?.name ?? "—",
          u.enabled ? "Yes" : "No", u.locked ? "Yes" : "No",
        ]),
        filename: "users",
        colWidths: [0.13, 0.16, 0.20, 0.16, 0.16, 0.10, 0.09],
      });
    } finally { setExporting(false); }
  }

  async function handleExcel() {
    setExportingExcel(true);
    try {
      await exportToExcel(
        "User Administration",
        ["Username", "Full Name", "Email", "Employee No.", "Job Title", "Primary Dept", "Enabled", "Locked", "Must Change Password"],
        users.map((u) => [
          u.username, u.fullName ?? "", u.email,
          u.employee?.employeeNumber ?? "",
          u.employee?.jobTitle ?? "—",
          u.employee?.departmentName ?? u.departments?.[0]?.name ?? "—",
          u.enabled ? "Yes" : "No", u.locked ? "Yes" : "No", u.mustChangePassword ? "Yes" : "No",
        ]),
        "users",
      );
    } finally { setExportingExcel(false); }
  }

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">User Administration</h1>
          <p className="text-sm text-slate-500">
            <span className="font-bold text-slate-700">{usersQuery.data?.totalElements ?? 0}</span> system users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handlePdf} disabled={exporting || isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-red-50 disabled:opacity-40">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-500" />}
            PDF
          </button>
          <button type="button" onClick={handleExcel} disabled={exportingExcel || isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-emerald-50 disabled:opacity-40">
            {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-emerald-600" />} Excel
          </button>
        </div>
      </div>

      {/* Stats chips */}
      {!isLoading && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Total", value: usersQuery.data?.totalElements ?? 0, cls: "bg-white border-slate-200 text-slate-700" },
            { label: "Enabled", value: users.filter((u) => u.enabled).length, cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { label: "Locked", value: users.filter((u) => u.locked).length, cls: "bg-red-50 border-red-200 text-red-800" },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl border px-3 py-1.5 text-xs font-bold ${s.cls}`}>
              {s.label}: {s.value}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid gap-2 rounded-2xl border bg-white p-3 shadow-sm sm:grid-cols-4">
        <div className="relative sm:col-span-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Name, username, email…"
            className="w-full rounded-xl border py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <select value={enabled === "" ? "" : String(enabled)}
          onChange={(e) => { setEnabled(e.target.value === "" ? "" : e.target.value === "true"); setPage(0); }}
          className="rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500">
          <option value="">All (enabled / disabled)</option>
          <option value="true">Enabled only</option>
          <option value="false">Disabled only</option>
        </select>
        <select value={departmentCode} onChange={(e) => { setDepartmentCode(e.target.value); setPage(0); }}
          className="rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500">
          <option value="">All departments</option>
          {(deptsQuery.data ?? []).map((d) => <option key={d.id} value={d.code}>{d.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b bg-slate-50">
              <tr className="text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5 text-left">User</th>
                <th className="px-4 py-2.5 text-left">Job Title</th>
                <th className="px-4 py-2.5 text-left">Primary Department</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} className="py-10 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="py-12 text-center">
                  <Users className="mx-auto mb-2 h-7 w-7 text-slate-300" />
                  <p className="text-sm text-slate-500">No users match the current filters.</p>
                </td></tr>
              ) : users.map((user) => <UserTableRow key={user.id} user={user} />)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!isLoading && (usersQuery.data?.totalPages ?? 0) > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-2.5 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span><span className="font-bold text-slate-800">{usersQuery.data?.totalElements ?? 0}</span> users</span>
            <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
              className="rounded-lg border px-2 py-1 text-xs outline-none focus:border-emerald-500">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <PgBtn onClick={() => setPage(0)} disabled={page === 0} label="«" />
            <PgBtn onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0} label="‹" />
            {pageNums.map((p, i) =>
              p === "…" ? <span key={`e${i}`} className="px-1 text-xs text-slate-400">…</span> :
                <button key={p} type="button" onClick={() => setPage(p)}
                  className={`flex h-7 w-7 items-center justify-center rounded-lg border text-xs font-bold transition ${p === page ? "border-emerald-600 bg-emerald-600 text-white" : "hover:bg-slate-50"}`}>
                  {p + 1}
                </button>
            )}
            <PgBtn onClick={() => setPage((p) => p + 1)} disabled={usersQuery.data?.last ?? true} label="›" />
            <PgBtn onClick={() => setPage((usersQuery.data?.totalPages ?? 1) - 1)} disabled={usersQuery.data?.last ?? true} label="»" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── User table row ───────────────────────────────────────────────────────────

function UserTableRow({ user }: { user: UserRow }) {
  const displayName = user.fullName || user.employee?.fullName || user.username;
  // Primary department: prefer linked employee's departmentName, fallback to departments array
  const primaryDept =
    user.employee?.departmentName ||
    user.departments?.find((d) => d.id === user.employee?.primaryDepartmentId)?.name ||
    user.departments?.[0]?.name ||
    user.departmentCodes?.[0] ||
    null;

  const jobTitle = user.employee?.jobTitle || null;

  return (
    <tr className="border-t text-sm hover:bg-slate-50/60">

      {/* User identity */}
      <td className="px-4 py-2">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-black text-white ${avatarColor(displayName)}`}>
            {initials(displayName)}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-slate-900 text-sm">{displayName}</div>
            <div className="text-xs text-slate-400">@{user.username}</div>
            <div className="truncate text-xs text-slate-400 max-w-[200px]">{user.email}</div>
          </div>
        </div>
      </td>

      {/* Job Title */}
      <td className="px-4 py-2">
        <span className="text-sm text-slate-700">{jobTitle || <span className="text-slate-400">—</span>}</span>
      </td>

      {/* Primary Department */}
      <td className="px-4 py-2">
        {primaryDept ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
            {primaryDept}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-2">
        <div className="flex flex-col gap-1">
          <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${user.enabled ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
            {user.enabled ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {user.enabled ? "Enabled" : "Disabled"}
          </span>
          {user.locked && (
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">
              <Lock className="h-3 w-3" /> Locked
            </span>
          )}
          {user.mustChangePassword && (
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-700">
              <ShieldAlert className="h-3 w-3" /> Must reset
            </span>
          )}
        </div>
      </td>

      {/* Action */}
      <td className="px-4 py-2 text-right">
        <Link to={`/admin/users/${user.id}`}
          className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-50">
          <ShieldCheck className="h-3.5 w-3.5" /> View Details
        </Link>
      </td>
    </tr>
  );
}

function PgBtn({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="flex h-7 w-7 items-center justify-center rounded-lg border text-xs font-bold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
      {label}
    </button>
  );
}
